import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { BoltOutlined, LockOutlined, PersonOutline, ScienceOutlined, ShieldOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../auth';
import { errorMessage } from '../services/api';
import { BrandMark } from '../components/common/Layout';
import { t } from '../utils/theme';

const features = [
  { icon: <ShieldOutlined />, title: 'Hard daily loss cap', text: 'Every trade is sized so the day can never lose more than your limit.' },
  { icon: <BoltOutlined />, title: 'Fully automatic', text: 'Picks NIFTY 50 stocks at 09:30, trades the breakout, squares off by 15:10.' },
  { icon: <ScienceOutlined />, title: 'Paper first', text: 'Run on live Kite prices with pretend fills before risking real money.' },
];

const Login: React.FC = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  if (user) return <Navigate to={from} replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(username.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(errorMessage(err, 'Login failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, bgcolor: t.bg }}>
      {/* Brand panel */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column', justifyContent: 'space-between', p: 6,
        position: 'relative', overflow: 'hidden', borderRight: `1px solid ${t.border}`,
        background: `radial-gradient(900px 500px at 0% 0%, rgba(99,102,241,0.25), transparent 60%),
                     radial-gradient(700px 400px at 100% 100%, rgba(31,175,85,0.14), transparent 60%), ${t.sidebar}`,
        '&::after': {
          content: '""', position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.35,
          backgroundImage: `linear-gradient(${t.border} 1px, transparent 1px), linear-gradient(90deg, ${t.border} 1px, transparent 1px)`,
          backgroundSize: '48px 48px', maskImage: 'linear-gradient(180deg, transparent, black 30%, black 70%, transparent)',
        },
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
          <BrandMark size={40} />
          <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>Zerodha Algo</Typography>
        </Stack>
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 520 }}>
          <Typography variant="h4" sx={{ fontSize: '2.4rem', lineHeight: 1.15, mb: 2 }}>
            Rules-based intraday trading,{' '}
            <Box component="span" sx={{ background: 'linear-gradient(90deg,#818CF8,#4ADE80)', WebkitBackgroundClip: 'text', color: 'transparent' }}>
              with the risk decided first.
            </Box>
          </Typography>
          <Typography sx={{ color: t.text2, mb: 5 }}>
            Opening-range breakout on your Kite account. You set the limits; the engine never breaks them.
          </Typography>
          <Stack spacing={2.5}>
            {features.map((f) => (
              <Stack key={f.title} direction="row" spacing={2} alignItems="flex-start">
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'rgba(99,102,241,0.14)', color: t.accentSoft, display: 'flex' }}>{f.icon}</Box>
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>{f.title}</Typography>
                  <Typography variant="body2" sx={{ color: t.text2 }}>{f.text}</Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Box>
        <Typography variant="caption" sx={{ color: t.text3, position: 'relative', zIndex: 1 }}>
          Trading involves risk. Paper results do not guarantee live profits.
        </Typography>
      </Box>

      {/* Form */}
      <Box sx={{ display: 'grid', placeItems: 'center', p: { xs: 2.5, sm: 4 } }}>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%', maxWidth: 380 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ display: { md: 'none' }, mb: 4 }}>
            <BrandMark size={36} />
            <Typography variant="h6">Zerodha Algo</Typography>
          </Stack>
          <Typography variant="h5" sx={{ mb: 0.5 }}>Sign in</Typography>
          <Typography variant="body2" sx={{ color: t.text2, mb: 3.5 }}>Private console. Authorised user only.</Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }} role="alert">{error}</Alert>}
          <Stack spacing={2}>
            <TextField
              fullWidth required autoFocus label="Login ID" autoComplete="username" value={username}
              onChange={(e) => setUsername(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutline sx={{ color: t.text3 }} /></InputAdornment> }}
            />
            <TextField
              fullWidth required label="Password" autoComplete="current-password"
              type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockOutlined sx={{ color: t.text3 }} /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end"
                                aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" fullWidth variant="contained" size="large" disabled={busy || !username || !password}
                    sx={{ py: 1.4, mt: 1 }}>
              {busy ? <CircularProgress size={22} color="inherit" /> : 'Sign in'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
